'use client'

import { useState, useCallback, useEffect } from 'react'

import { useChat } from 'ai/react'
import { customAlphabet } from 'nanoid'

import Header from 'components/head'

import usePersistedState from 'hooks/usePersistedState'
const nanoid = customAlphabet(
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
  24
)

const apiUrl = process.env.NEXT_PUBLIC_API_URL

const Chat = ({ t }) => {
  const [loading, setLoading] = useState(false)
  const [persistVal, setPersistVal] = usePersistedState('_idD0-hes')
  const id = persistVal || `chat:${nanoid()}`
  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    setMessages
  } = useChat({
    api: process.env.NEXT_PUBLIC_API_URL + '/me/',
    headers: {
      'Coversation-Idd0-Hes': persistVal || id
    }
  })

  const getChat = useCallback(async () => {
    if (persistVal && !messages?.length) {
      const url = process.env.NEXT_PUBLIC_API_URL + '/getMeChat/'
      setLoading(true)
      try {
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify({ idMessage: persistVal }),
          headers: {
            'Content-type': 'application/json; charset=UTF-8'
          }
        })
        const json = await response.json()
        const chatResponse = json.response
        const chatID = json.data
        setPersistVal(chatID)
        setMessages(chatResponse)
      } catch (error) {
        console.log({ error })
        setPersistVal(null)
      } finally {
        setLoading(false)
      }
    }
  }, [persistVal, setPersistVal, messages, setMessages])

  useEffect(() => {
    getChat()
  }, [getChat])

  const handleSend = (event) => {
    setPersistVal(id)
    handleSubmit(event)
  }

  return (
    <>
      <Header
        title="That's Me"
        description="That's Me - Joca.dev"
        image={`${apiUrl}/og/ai/?title=Johnny's AI`}
        height={375}
        width={750}
      />
      <div className="container">
        <div className="chat">
          <div className="messages">
            {(!!messages?.length && (
              <>
                <div className="titleDetail">
                  <h1>{t.title_AI}</h1>
                </div>
                {messages?.map((item, index) => (
                  <div key={index} className="message">
                    <div
                      className={
                        item.role === 'system' || item.role === 'user'
                          ? 'text me'
                          : 'text gtp'
                      }
                    >
                      {item.content}
                    </div>
                  </div>
                ))}
              </>
            )) ||
              (!isLoading && !persistVal && (
                <div className="welcome">
                  <div>
                    <p>{t.present_AI}</p>
                    <p>{t.present_AI_2}</p>
                    <p>{t.present_AI_3}</p>
                  </div>
                </div>
              ))}
            {isLoading && (
              <div className="text">
                <p className="typing">
                  {loading ? (
                    <>
                      <span>{t.typing}... </span>
                      <button
                        className="reset"
                        onClick={() => console.log('cancel')}
                      >
                        {t.cancel}
                      </button>
                    </>
                  ) : (
                    ''
                  )}
                </p>
              </div>
            )}
          </div>
          <div className="input">
            <textarea
              className="textArea"
              onChange={handleInputChange}
              disabled={isLoading}
              placeholder={t.write_your_message}
              value={input}
            />
            <button disabled={!input} className="submit" onClick={handleSend}>
              Send
            </button>
          </div>
        </div>
        <style jsx>{`
          .container {
            position: absolute;
            top: 0px;
            left: 0px;
            width: 100%;
            height: 100%;
            z-index: 91000;
            opacity: 1;
            overflow: hidden;
            display: block;
            background-color: white;
            transition: transform 0.2s ease 0s;
          }
          .chat {
            position: relative;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            padding: 20px;
          }

          .messages {
            overflow-y: auto;
            padding: 8px;
            flex-grow: 1;
            flex-shrink: 1;
          }
          .welcome {
            display: flex;
            gap: 1rem;
          }

          .gradient {
            max-width: 1000px;
            margin: 0 auto;
          }

          .titleDetail {
            text-align: center;
            margin-bottom: 30px;
          }

          .textArea {
            width: 100%;
            height: 100px;
            resize: none;
            margin-top: 10px;
            padding: 10px;
            box-sizing: border-box;
            font-size: 1.6rem;
          }

          .text {
            padding: 1rem;
            color: black;
            border-radius: 5px;
            margin-top: 5px;
            width: 90%;
            font-size: 1.6rem;
            line-height: initial;
          }

          .message:nth-child(even) .text {
            background-color: var(--primary);
            color: var(--black);
          }

          .message:nth-child(odd) {
            flex-direction: column;
            align-content: flex-end;
            flex-wrap: wrap;
            display: flex;
          }

          .message:nth-child(odd) .text {
            background-color: var(--me);
            color: var(--black);
          }

          .text :global(ol),
          .text :global(ul) {
            display: flex;
            flex-direction: column;
            padding-left: 4rem;
          }

          .text :global(pre) {
            margin: 1rem 2rem;
          }

          .text :global(li) {
            list-style-type: decimal;
            font-size: 1.6rem;
            line-height: 2rem;
          }

          .text :global(li::marker) {
            font-size: 1.6rem;
            font-weight: 400;
          }

          .typing span:after {
            animation: 'blink' 1s steps(5, start) infinite;
            content: '▋';
            margin-left: 0.25rem;
            vertical-align: baseline;
          }

          button {
            border: none;
            width: 100%;
            height: 6rem;
            font-size: 2rem;
            background: var(--primary);
            border-radius: 5px;
            margin-top: 1rem;
            color: var(--white);
            transition-duration: 0.4s;
            opacity: 1;
          }
          button:hover {
            cursor: pointer;
          }

          button.submit:disabled {
            background-color: var(--black);
            cursor: not-allowed;
          }

          :global(#chat-bubble-button),
          :global(#chat-bubble-window) {
            display: none;
          }

          .reset {
            background-color: transparent;
            border: 1px solid var(--black);
            padding: 0.2rem 1rem;
            width: 100px;
            color: var(--black);
            text-transform: lowercase;
            font-family: 'Source Code Pro', -apple-system, BlinkMacSystemFont,
              Avenir Next, Avenir, Helvetica, sans-serif;
            font-size: 1.6rem;
            line-height: 2.2rem;
            height: initial;
          }
          .reset:hover {
            color: #7e8ffe;
            border: 1px solid #7e8ffe;
          }

          @media (max-width: 768px) {
            .gradient {
              grid-template-columns: 100%;
            }
            .conversations {
              height: 10vh;
              width: 100%;
            }
            .text:nth-child(even),
            .text:nth-child(odd) {
              margin-right: 0;
              margin-left: 0;
            }
          }
        `}</style>
      </div>
    </>
  )
}

export async function getServerSideProps({ locale = 'es' }) {
  const response = await import(`../locales/${locale}.json`)
  const locales = response.default.me

  return { props: { t: locales } }
}

export default Chat
